import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useDarkMode } from '@/hooks/useDarkMode';
import { logger } from '@/utils/logger';
import { ClipboardList, Gift, Loader2, Sparkles, X } from 'lucide-react';

// Optional questionnaire filter for Browse: appears when the logged-in user
// has questionnaires with at least one response. Clicking a chip runs AI
// recommendations from the responses; unlinked questionnaires get a one-tap
// "Save to Keepsake" action.
export default function QuestionnaireFilterBar({ user, onResults, onLoadingChange, activeName, onClear }) {
  const darkMode = useDarkMode();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [runningId, setRunningId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [qRes, rRes] = await Promise.all([api.get('/keepsakes'), api.get('/recipients')]);
        setQuestionnaires((qRes.data || []).filter(q => (q.response_count || 0) > 0));
        setRecipients(rRes.data || []);
      } catch (err) {
        logger.error('Failed to load questionnaires for filter bar:', err);
      }
    })();
  }, [user]);

  if (!user || dismissed || questionnaires.length === 0) return null;

  const hasKeepsake = (q) =>
    recipients.some(r => r.name.toLowerCase() === q.recipient_name.toLowerCase());

  const runFilter = async (q) => {
    setRunningId(q.id);
    onLoadingChange?.(true);
    try {
      const res = await api.get(`/keepsakes/${q.id}/responses`);
      const answers = (res.data || []).flatMap(r => r.answers || []);
      if (answers.length === 0) {
        toast.info('No answers found on this questionnaire yet');
        return;
      }
      const rec = await api.post('/ai-recommendations', { answers });
      const recommendations = rec.data.recommendations || [];
      if (recommendations.length === 0) {
        toast.info('No matches found from these answers, try Browse filters instead');
        return;
      }
      onResults(
        recommendations,
        rec.data.personality_summary || '',
        q.recipient_name,
        rec.data.creative_alternatives || []
      );
    } catch (err) {
      logger.error('Questionnaire filter failed:', err);
      toast.error('Could not build recommendations from this questionnaire');
    } finally {
      setRunningId(null);
      onLoadingChange?.(false);
    }
  };

  const saveToKeepsake = async (q) => {
    setSavingId(q.id);
    try {
      const res = await api.post(`/keepsakes/${q.id}/save-to-keepsake`, {});
      setRecipients(prev => [...prev, res.data.recipient]);
      toast.success(
        res.data.created
          ? `Keepsake created for ${q.recipient_name}!`
          : `Linked to ${q.recipient_name}'s keepsake`
      );
    } catch (err) {
      logger.error('Save to keepsake failed:', err);
      toast.error('Failed to save to keepsake');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 pt-4"
      data-testid="questionnaire-filter-bar"
    >
      <div
        className="rounded-xl px-4 py-3 flex flex-wrap items-center gap-2"
        style={{
          background: darkMode.isDark ? 'rgba(254,215,165,0.08)' : '#FFF8F0',
          border: `1px solid ${darkMode.isDark ? 'rgba(254,215,165,0.25)' : '#FED7A5'}`
        }}
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold tracking-wide mr-1" style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}>
          <ClipboardList className="w-4 h-4" />
          FILTER BY QUESTIONNAIRE
        </span>

        {questionnaires.map(q => (
          <div key={q.id} className="flex items-center gap-1">
            <button
              onClick={() => runFilter(q)}
              disabled={runningId !== null}
              data-testid={`questionnaire-filter-chip-${q.id}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-[1.03]"
              style={{
                background: activeName === q.recipient_name ? '#9E6752' : (darkMode.isDark ? 'rgba(255,255,255,0.08)' : 'white'),
                color: activeName === q.recipient_name ? 'white' : (darkMode.isDark ? '#FFFAF5' : '#534145'),
                border: `1px solid ${activeName === q.recipient_name ? '#9E6752' : (darkMode.isDark ? 'rgba(254,215,165,0.3)' : '#E5D5C5')}`
              }}
            >
              {runningId === q.id
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5" style={{ color: activeName === q.recipient_name ? '#FED7A5' : '#9E6752' }} />}
              {q.recipient_name}
              <span className="text-xs opacity-70">· {q.response_count}</span>
            </button>
            {!hasKeepsake(q) && (
              <button
                onClick={() => saveToKeepsake(q)}
                disabled={savingId === q.id}
                data-testid={`questionnaire-save-keepsake-${q.id}`}
                title={`Save ${q.recipient_name}'s questionnaire to a keepsake`}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-[1.03]"
                style={{
                  background: darkMode.isDark ? 'rgba(45,67,84,0.6)' : '#2D4354',
                  color: '#FED7A5'
                }}
              >
                {savingId === q.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />}
                Save to Keepsake
              </button>
            )}
          </div>
        ))}

        {activeName && (
          <button
            onClick={onClear}
            data-testid="questionnaire-filter-clear"
            className="ml-auto flex items-center gap-1 text-xs hover:underline"
            style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
          >
            <X className="w-3.5 h-3.5" /> Clear filter
          </button>
        )}
        {!activeName && (
          <button
            onClick={() => setDismissed(true)}
            aria-label="Hide questionnaire filters"
            className="ml-auto p-1 rounded-full transition-colors"
            style={{ color: darkMode.isDark ? 'rgba(254,215,165,0.6)' : '#B0A091' }}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
